import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  acceptCompanyClaimInvitation,
  companyOwnerSignIn,
  companyOwnerSignUp,
  fetchCompanyPayoutDetails,
  previewCompanyClaimInvitation,
  resendCompanyOwnerEmailOtp,
  verifyCompanyOwnerEmail,
} from '../services/api/companyOwnerAuth';
import {
  clearCompanyOwnerClaimToken,
  clearCompanyOwnerAuthFlow,
  getCompanyOwnerClaimToken,
  saveCompanyOwnerAuthTokens,
  saveCompanyOwnerAuthUser,
  saveCompanyOwnerClaimToken,
  saveCompanyOwnerCompany,
  saveCompanyOwnerPayoutDetails,
} from '../services/storage/companyOwnerFlow';
import { formatDateTime, getCompanyOwnerNextRoute, tokenHasCompanyOwnerRole } from '../utils/companyOwner';
import type { CompanyClaimInvitationPreview } from '../types/companyOwner';
import '../styles/register.css';

interface SnackbarState {
  message: string;
  tone: 'success' | 'error' | 'info';
}

const CompanyOwnerRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClaimToken = searchParams.get('claimToken')?.trim() || '';
  const [claimToken, setClaimToken] = useState(queryClaimToken || getCompanyOwnerClaimToken() || '');
  const [claimPreview, setClaimPreview] = useState<CompanyClaimInvitationPreview | null>(null);
  const [claimPreviewLoading, setClaimPreviewLoading] = useState(Boolean(queryClaimToken || getCompanyOwnerClaimToken()));
  const [claimPreviewError, setClaimPreviewError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);

  useEffect(() => {
    if (!claimToken) {
      setClaimPreview(null);
      setClaimPreviewLoading(false);
      setClaimPreviewError('');
      return;
    }

    saveCompanyOwnerClaimToken(claimToken);
    let mounted = true;

    const loadPreview = async () => {
      setClaimPreviewLoading(true);
      setClaimPreviewError('');

      try {
        const preview = await previewCompanyClaimInvitation(claimToken);
        if (!mounted) return;
        setClaimPreview(preview);
      } catch (error: any) {
        if (!mounted) return;
        setClaimPreview(null);
        setClaimPreviewError(error.message || 'Could not load the invitation preview.');
      } finally {
        if (mounted) {
          setClaimPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      mounted = false;
    };
  }, [claimToken]);

  useEffect(() => {
    if (!snackbar) return undefined;
    const timeoutId = window.setTimeout(() => setSnackbar(null), 4500);
    return () => window.clearTimeout(timeoutId);
  }, [snackbar]);

  const loginPath = claimToken
    ? `/login?role=company&claimToken=${encodeURIComponent(claimToken)}`
    : '/login?role=company';

  const setField =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((current) => ({ ...current, [field]: event.target.value }));
      setErrors((current) => {
        if (!current[field]) return current;
        const nextErrors = { ...current };
        delete nextErrors[field];
        return nextErrors;
      });
    };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      nextErrors.fullName = 'Enter the account owner full name.';
    }

    const normalizedEmail = formData.email.trim().toLowerCase();
    if (!normalizedEmail) {
      nextErrors.email = 'Enter the invited company email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!formData.password.trim()) {
      nextErrors.password = 'Create a password for this account.';
    } else if (formData.password.trim().length < 8) {
      nextErrors.password = 'Use at least 8 characters.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const acceptInvitationAfterAuth = async (): Promise<void> => {
    if (!claimToken) {
      navigate('/company/onboarding', { replace: true });
      return;
    }

    let company;
    try {
      company = await acceptCompanyClaimInvitation(claimToken);
    } catch (error: any) {
      throw new Error(
        `Your email was verified, but we could not attach this company invitation. ${
          error.message || 'Please contact ShiftWagon support to review the claim.'
        }`,
      );
    }
    saveCompanyOwnerCompany(company);
    clearCompanyOwnerClaimToken();
    try {
      const payoutDetails = await fetchCompanyPayoutDetails();
      saveCompanyOwnerPayoutDetails(payoutDetails);
      navigate(getCompanyOwnerNextRoute(company, payoutDetails), { replace: true });
    } catch {
      navigate(getCompanyOwnerNextRoute(company), { replace: true });
    }
  };

  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setErrors({});

    try {
      clearCompanyOwnerAuthFlow();
      saveCompanyOwnerClaimToken(claimToken);

      const response = await companyOwnerSignUp({
        name: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: ['ROLE_COMPANY_OWNER'],
        signUpMethod: 'manual',
      });

      if (!response.success) {
        throw new Error(response.message || 'Could not create the company owner account.');
      }

      setShowEmailModal(true);
      setSnackbar({
        message: response.message || 'Account created. Enter the email code to continue.',
        tone: 'success',
      });
    } catch (error: any) {
      setErrors({ submit: error.message || 'Could not create the company owner account.' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!emailCode.trim()) {
      setEmailError('Enter the 6-digit code from your email.');
      return;
    }

    setEmailLoading(true);
    setEmailError('');

    try {
      const response = await verifyCompanyOwnerEmail(
        formData.email.trim().toLowerCase(),
        emailCode.trim(),
      );

      if (!response.success || !response.userResponse) {
        throw new Error(response.message || 'Could not verify your email.');
      }

      let tokenToUse = response.accessToken ?? response.token;
      let refreshTokenToUse = response.refreshToken;
      let accessTokenExpiresAt = response.accessTokenExpiresAt;
      let refreshTokenExpiresAt = response.refreshTokenExpiresAt;
      let userToUse = response.userResponse;

      if (!tokenToUse || !tokenHasCompanyOwnerRole(tokenToUse)) {
        const loginResponse = await companyOwnerSignIn({
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          role: 'company',
        });
        const loginAccessToken = loginResponse.accessToken ?? loginResponse.token;

        if (!loginResponse.success || !loginAccessToken || !loginResponse.refreshToken || !loginResponse.userResponse) {
          throw new Error(
            loginResponse.message ||
              'Your email was verified, but we could not establish the company-owner session.',
          );
        }

        tokenToUse = loginAccessToken;
        refreshTokenToUse = loginResponse.refreshToken;
        accessTokenExpiresAt = loginResponse.accessTokenExpiresAt;
        refreshTokenExpiresAt = loginResponse.refreshTokenExpiresAt;
        userToUse = loginResponse.userResponse;
      }

      if (!tokenToUse || !refreshTokenToUse) {
        throw new Error('Your email was verified, but no login token was returned.');
      }

      saveCompanyOwnerAuthTokens({
        accessToken: tokenToUse,
        refreshToken: refreshTokenToUse,
        accessTokenExpiresAt,
        refreshTokenExpiresAt,
      });
      saveCompanyOwnerAuthUser(userToUse);
      setShowEmailModal(false);
      await acceptInvitationAfterAuth();
    } catch (error: any) {
      setEmailError(error.message || 'Could not verify your email.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setEmailLoading(true);
    setEmailError('');

    try {
      await resendCompanyOwnerEmailOtp(formData.email.trim().toLowerCase());
      setSnackbar({ message: 'A fresh verification code has been sent.', tone: 'info' });
    } catch (error: any) {
      setEmailError(error.message || 'Could not resend the email code.');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCloseEmailModal = () => {
    setShowEmailModal(false);
    setEmailCode('');
    setEmailError('');
  };

  return (
    <main className="register-page">
      <div className="register-shell register-shell-single">
        <aside className="register-panel register-panel-brand">
          <div className="register-brand-lockup">
            <div className="register-brand-badge">ShiftWagon</div>
            <h1 className="register-brand-title">Company owner access is invitation-based.</h1>
            <p className="register-brand-copy">
              A company driver must onboard first and use your company email address. ShiftWagon
              then emails you a one-time invitation link to create or connect your company owner
              account safely.
            </p>
          </div>

          {claimPreviewLoading ? (
            <div className="register-highlight-card">
              <div className="register-loader" />
              <p className="register-highlight-copy">
                Loading your company invitation preview...
              </p>
            </div>
          ) : claimPreview ? (
            <div className="register-highlight-card">
              <div className="register-highlight-row">
                <span className="register-highlight-icon">C</span>
                <div>
                  <div className="register-highlight-title">Invitation Ready</div>
                  <div className="register-highlight-note">{claimPreview.companyName}</div>
                </div>
              </div>
              <p className="register-highlight-copy">
                You&apos;ve been invited to claim {claimPreview.companyName} on ShiftWagon.
              </p>
              <div className="register-benefit-list" style={{ marginTop: '18px' }}>
                <div className="register-benefit-item">
                  <span className="register-benefit-dot" />
                  <span>Invited email: {claimPreview.maskedCompanyEmail}</span>
                </div>
                <div className="register-benefit-item">
                  <span className="register-benefit-dot" />
                  <span>
                    Expires: {claimPreview.expiresAt ? formatDateTime(claimPreview.expiresAt) : 'Not available'}
                  </span>
                </div>
                {claimPreview.invitedBy ? (
                  <div className="register-benefit-item">
                    <span className="register-benefit-dot" />
                    <span>Invited by: {claimPreview.invitedBy}</span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="register-highlight-card">
              <div className="register-highlight-row">
                <span className="register-highlight-icon">i</span>
                <div>
                  <div className="register-highlight-title">Company Invitation Required</div>
                  <div className="register-highlight-note">Driver-seeded access only</div>
                </div>
              </div>
              <p className="register-highlight-copy">
                Ask one of your company drivers to complete ShiftWagon onboarding with your company
                email address. We&apos;ll send a claim invitation to that inbox.
              </p>
            </div>
          )}

          <div className="register-benefit-list">
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>The claim link is one-time use and tied to the invited company email.</span>
            </div>
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>Company attachment now happens after authentication and invitation acceptance.</span>
            </div>
            <div className="register-benefit-item">
              <span className="register-benefit-dot" />
              <span>Existing company owners can still log in normally if they already have an account.</span>
            </div>
          </div>

          <div className="register-app-card">
            <div className="register-app-card-title">Already have an account?</div>
            <div className="register-app-card-copy">
              Log in with the invited company email address and we&apos;ll complete the claim after sign-in.
            </div>
            <div className="register-flow-actions" style={{ justifyContent: 'flex-start' }}>
              <Link className="register-secondary-button" to={loginPath}>
                Company Login
              </Link>
            </div>
          </div>
        </aside>

        <div className="register-panel register-panel-form">
          <div className="register-form-header">
            <div className="register-form-badge">Company Owner Registration</div>
            <h1 className="register-form-title">
              {claimToken ? 'Create your invited company owner account' : 'Invitation required before account creation'}
            </h1>
            <p className="register-form-copy">
              {claimToken
                ? 'Sign up with the invited company email address, verify your email, and we will attach your account to the invited company automatically.'
                : 'Public company claiming is no longer open. Use the invitation email sent after one of your drivers completes company-driver onboarding.'}
            </p>
          </div>

          {!claimToken ? (
            <div className="register-form">
              <div className="register-inline-error">
                No company invitation link was found. Ask your driver to onboard first and use your company email address.
              </div>
              <div className="register-flow-actions">
                <Link className="register-secondary-button" to="/">
                  Go Home
                </Link>
                <Link className="register-submit register-submit-inline" to="/login?role=company">
                  Existing company owner login
                </Link>
              </div>
            </div>
          ) : claimPreviewError ? (
            <div className="register-form">
              <div className="register-inline-error">{claimPreviewError}</div>
              <div className="register-flow-actions">
                <Link className="register-secondary-button" to="/">
                  Go Home
                </Link>
                <Link className="register-submit register-submit-inline" to={loginPath}>
                  Try company login
                </Link>
              </div>
            </div>
          ) : (
            <form className="register-form" onSubmit={handleRegister}>
              <div className="register-field-group">
                <div className="register-field">
                  <label className="register-label" htmlFor="companyOwnerName">
                    Full Name
                  </label>
                  <input
                    className="register-input"
                    id="companyOwnerName"
                    onChange={setField('fullName')}
                    placeholder="e.g. Zinhle Maseko"
                    value={formData.fullName}
                  />
                  {errors.fullName ? <div className="register-inline-error">{errors.fullName}</div> : null}
                </div>

                <div className="register-field">
                  <label className="register-label" htmlFor="companyOwnerEmail">
                    Invited Company Email
                  </label>
                  <input
                    autoCapitalize="none"
                    className="register-input"
                    id="companyOwnerEmail"
                    onChange={setField('email')}
                    placeholder="owner@company.com"
                    type="email"
                    value={formData.email}
                  />
                  <div className="register-field-hint">
                    Use the same company email address that received this invitation.
                  </div>
                  {errors.email ? <div className="register-inline-error">{errors.email}</div> : null}
                </div>

                <div className="register-field">
                  <label className="register-label" htmlFor="companyOwnerPassword">
                    Password
                  </label>
                  <div className="register-password-shell">
                    <input
                      className="register-input register-input-password"
                      id="companyOwnerPassword"
                      onChange={setField('password')}
                      placeholder="Create a strong password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                    />
                    <button
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="register-password-toggle"
                      onClick={() => setShowPassword((current) => !current)}
                      type="button"
                    >
                      {showPassword ? (
                        <svg aria-hidden="true" className="register-password-icon" fill="none" viewBox="0 0 24 24">
                          <path d="M3 3l18 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                          <path d="M10.58 10.58A2 2 0 0013.42 13.42" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                          <path d="M9.88 5.09A10.94 10.94 0 0112 4.9c5.05 0 8.27 4.13 9 5.1a15.63 15.63 0 01-4.05 3.81" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                          <path d="M6.61 6.61C4.62 7.84 3.32 9.51 3 10c.5.66 2.15 2.79 4.82 4.13A11.8 11.8 0 0012 15.1c.64 0 1.25-.05 1.83-.14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                        </svg>
                      ) : (
                        <svg aria-hidden="true" className="register-password-icon" fill="none" viewBox="0 0 24 24">
                          <path d="M2.46 12.29C3.74 10.18 6.89 6 12 6s8.26 4.18 9.54 6.29a.56.56 0 010 .58C20.26 14.98 17.11 19 12 19s-8.26-4.02-9.54-6.13a.56.56 0 010-.58z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                          <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="register-field-hint">Use at least 8 characters.</div>
                  {errors.password ? <div className="register-inline-error">{errors.password}</div> : null}
                </div>
              </div>

              {errors.submit ? <div className="register-inline-error">{errors.submit}</div> : null}

              <button className="register-submit" disabled={loading || claimPreviewLoading} type="submit">
                {loading ? 'Creating company owner account...' : 'Create Company Owner Account'}
              </button>

              <p className="register-login-copy">
                Already have a company owner account?{' '}
                <Link className="register-login-link" to={loginPath}>
                  Log in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>

      {showEmailModal ? (
        <div className="register-modal-backdrop" role="presentation">
          <div
            aria-labelledby="company-owner-email-modal-title"
            aria-modal="true"
            className="register-modal"
            role="dialog"
          >
            <button
              aria-label="Close email verification"
              className="register-modal-close"
              onClick={handleCloseEmailModal}
              type="button"
            >
              ×
            </button>
            <div className="register-status-badge">Verify Email</div>
            <h2 className="register-modal-title" id="company-owner-email-modal-title">
              Enter the code from your email
            </h2>
            <p className="register-modal-copy">
              We sent a 6-digit code to {formData.email.trim().toLowerCase()}.
            </p>
            <input
              className="register-input register-modal-input"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => {
                setEmailCode(event.target.value.replace(/\D/g, ''));
                if (emailError) setEmailError('');
              }}
              placeholder="123456"
              value={emailCode}
            />
            {emailError ? <div className="register-inline-error">{emailError}</div> : null}
            <div className="register-modal-actions">
              <button className="register-submit register-submit-inline" disabled={emailLoading} onClick={handleVerifyEmail} type="button">
                {emailLoading ? 'Verifying...' : 'Verify email'}
              </button>
              <button className="register-text-button" disabled={emailLoading} onClick={handleResendEmail} type="button">
                Resend code
              </button>
              <button className="register-text-button" disabled={emailLoading} onClick={handleCloseEmailModal} type="button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {snackbar ? <div className={`register-snackbar register-snackbar-${snackbar.tone}`}>{snackbar.message}</div> : null}
    </main>
  );
};

export default CompanyOwnerRegisterPage;
